import React from "react";
import { Truck, Package, Mail, Calendar, RefreshCw } from "lucide-react";

export default function Returns() {
  return (
    <div data-testid="returns-page" className="bg-ink text-white pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-mono text-xs uppercase tracking-[0.4em] text-adrenaline">Customer Care</div>
        <h1 className="text-display text-6xl md:text-8xl uppercase leading-none mt-3">Returns & Refunds</h1>
        <p className="text-zinc-400 mt-6 text-lg">We stand behind every drop. Here's how returns and refunds work — straight up, no fine print.</p>
        <div className="section-divider mt-12 mb-12" />

        {/* Pre-order notice */}
        <div className="border border-adrenaline/40 bg-adrenaline/5 p-6 mb-12">
          <div className="flex items-center gap-3 mb-3">
            <Calendar className="text-adrenaline" size={20} />
            <div className="text-heading uppercase tracking-wider text-adrenaline">Pre-Order Drops</div>
          </div>
          <p className="text-zinc-300 leading-relaxed">
            Most Lone Star Speed releases are <strong className="text-white">limited pre-order drops</strong>. When you order, the shirt goes into the print run. Shipping typically happens <strong className="text-white">3–5 weeks after the drop closes</strong>. You'll get tracking by email as soon as your order ships.
          </p>
        </div>

        <Section icon={RefreshCw} title="30-Day Return Window">
          <p>You have <strong className="text-white">30 days from the delivery date</strong> to request a return. Items must be unworn, unwashed, and in original packaging with tags attached.</p>
        </Section>

        <Section icon={Package} title="What Can Be Returned">
          <ul className="space-y-2 list-disc list-inside marker:text-adrenaline">
            <li>Unworn, unwashed apparel with original tags</li>
            <li>Defective items (printing flaws, stitching issues, wrong size shipped)</li>
            <li>Items damaged in shipping</li>
          </ul>
        </Section>

        <Section icon={Calendar} title="What Can't Be Returned">
          <ul className="space-y-2 list-disc list-inside marker:text-zinc-500">
            <li>Worn or washed items</li>
            <li>Items without original tags</li>
            <li>Items requested after the 30-day window</li>
            <li>Pre-order cancellations more than 48 hours after order placement (we lock the print run quickly)</li>
          </ul>
        </Section>

        <Section icon={Truck} title="How to Start a Return">
          <ol className="space-y-3 list-decimal list-inside marker:text-adrenaline">
            <li>Email <a href="mailto:support@lonestarspeedco.com" className="text-white underline">support@lonestarspeedco.com</a> within 30 days of delivery</li>
            <li>Include your order number + reason for return</li>
            <li>We'll email you a <strong className="text-white">prepaid USPS shipping label</strong> within 48 hours</li>
            <li>Print the label, drop the package at any USPS location</li>
            <li>Refund processes within 5–7 business days after we receive the item</li>
          </ol>
        </Section>

        <Section icon={RefreshCw} title="Refunds">
          <p>Refunds go back to your original payment method (card, Apple Pay, etc.). Bank processing usually takes <strong className="text-white">5–10 business days</strong> after we initiate the refund. Shipping costs are non-refundable unless the return is due to our error (wrong item, defect, damage).</p>
        </Section>

        <Section icon={Package} title="Exchanges">
          <p>Need a different size? Same drill — email us within 30 days. We'll send a prepaid label, and once we receive the original, we ship the exchange free of charge. Exchanges depend on stock availability.</p>
        </Section>

        <Section icon={Truck} title="Pre-Order Cancellations">
          <p>You can cancel a pre-order for a full refund within <strong className="text-white">48 hours of placing your order</strong>. After 48 hours we've already added you to the print run and the order is locked. Email <a href="mailto:support@lonestarspeedco.com" className="text-white underline">support@lonestarspeedco.com</a> within the window to cancel.</p>
        </Section>

        <Section icon={Mail} title="Damaged or Wrong Items">
          <p>If your order arrives damaged, defective, or wrong — we make it right immediately. Email us a photo of the issue within 7 days of delivery. We'll ship a replacement at no cost and you keep the original (or we'll send a prepaid return label, your call).</p>
        </Section>

        <div className="mt-16 p-8 bg-carbon border border-white/5 text-center">
          <div className="text-mono text-xs uppercase tracking-[0.4em] text-adrenaline mb-3">Questions?</div>
          <div className="text-display text-3xl text-white mb-4">We Got You.</div>
          <p className="text-zinc-400 mb-6">Real humans, real answers. We respond within 24 hours.</p>
          <a href="mailto:support@lonestarspeedco.com" className="inline-block px-8 py-3 bg-adrenaline text-white text-heading uppercase tracking-[0.2em] text-sm hover:bg-red-600 transition-colors btn-glow">
            support@lonestarspeedco.com
          </a>
        </div>

        <p className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600 mt-12 text-center">Last updated · {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <Icon className="text-adrenaline" size={20} />
        <h2 className="text-heading uppercase tracking-wider text-white text-xl">{title}</h2>
      </div>
      <div className="text-zinc-300 leading-relaxed pl-9">{children}</div>
    </div>
  );
}
